/**
 * Per-game dice luck, shared by the live player drawer and terminal summary.
 *
 * The chart follows incoming rolls while live and remains directly scrubbable.
 * Only the activity bolt animates, and reduced-motion users get a static bolt.
 */

import React from 'react'
import { HelpButton } from '../ui/HelpButton'
import {
  ActivityIndicator,
  BackHandler,
  PixelRatio,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
  type GestureResponderEvent,
  type LayoutChangeEvent,
} from 'react-native'
import Svg, { Circle, Line, Polyline, Text as SvgText } from 'react-native-svg'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated'
import type {
  DiceLuckPlayerSummary,
  DiceLuckResult,
  DiceLuckSnapshot,
  GamePlayerResponse,
  MapDef,
} from '@warcarp/api-types'
import {
  DICE_LUCK_PERCENTILE_MIN_ROLLS,
  diceLuckPercentile,
  diceLuckVerdict,
} from '@warcarp/api-types'
import { useTheme } from '../../hooks/useTheme'
import { useWebModalHistory } from '../../hooks/useWebModalHistory'
import {
  diceLuckVerdictToneClass,
  formatDiceLuckScore,
  formatDiceLuckSwing,
  formatDiceLuckVerdict,
  rankDiceLuckPlayers,
  withCurrentDiceLuckQualification,
} from '../../lib/diceLuck'
import { AppModal } from '../ui/AppModal'
import { DiceLuckHelp } from './DiceLuckHelp'
import { ModalCloseTab, ModalTabSurface } from '../ui/ModalCloseTab'
import { GameOverlayTopContext, floatingGamePanelBounds } from '../../lib/gameOverlayBounds'

interface Props {
  visible: boolean
  onClose: () => void
  players: GamePlayerResponse[]
  viewerPlayerId: string | null
  /** Player selected by the entry point; falls back to the viewer. */
  initialPlayerId?: string | null
  map: MapDef | null
  /** Full-log result, including the cumulative series and notable rolls. */
  result: DiceLuckResult | null
  /** Durable aggregate used after the detailed log has expired. */
  snapshot: DiceLuckSnapshot | null
  /** Whether this active game can return to a realtime chart. */
  canGoLive: boolean
  /** True only while the socket is connected and the full event log is caught up. */
  isLive: boolean
  lastUpdatedAt: number | null
  loading: boolean
  error: boolean
  unavailable: boolean
  onGoLive: () => void
  onViewEvent: (eventIndex: number, eventTs: number) => void
}


/**
 * The chart box must sit on whole physical pixels. Yoga rounds each edge
 * separately, so a height like 84pt (220.5px at 2.625 density) renders 220
 * or 221px depending on sub-pixel offsets from earlier layout passes, visible
 * as a shifted line and border in native screenshots. A no-op at 3x (iOS).
 */
function snapToPixel(points: number): number {
  return PixelRatio.roundToNearestPixel(points)
}

function displayVerdict(row: DiceLuckPlayerSummary): string {
  return formatDiceLuckVerdict(row.verdict)
}

export function formatLiveAge(updatedAt: number, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.floor((now - updatedAt) / 60_000))
  if (minutes < 1) return 'just now'
  return `${minutes} min${minutes === 1 ? '' : 's'} ago`
}

function DiceActivityStatus({
  activityAt,
  viewingAt,
  mode,
  onGoLive,
}: {
  activityAt: number
  viewingAt: number | null
  mode: 'live' | 'history' | 'syncing'
  onGoLive: () => void
}) {
  const { theme } = useTheme()
  const reducedMotion = useReducedMotion()
  const pulse = useSharedValue(0.25)
  const [now, setNow] = React.useState(() => Date.now())

  React.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [])

  React.useEffect(() => {
    cancelAnimation(pulse)
    const recentlyUpdated = Date.now() - activityAt <= 15_000
    let settleTimer: ReturnType<typeof setTimeout> | undefined

    if (reducedMotion) {
      pulse.value = recentlyUpdated ? 1 : 0.25
    } else if (recentlyUpdated) {
      pulse.value = 1
      pulse.value = withRepeat(
        withSequence(
          withTiming(0, { duration: 140, easing: Easing.inOut(Easing.quad) }),
          withTiming(1, { duration: 220, easing: Easing.inOut(Easing.quad) }),
          withTiming(0.25, { duration: 420, easing: Easing.inOut(Easing.quad) }),
          withTiming(1, { duration: 220, easing: Easing.inOut(Easing.quad) }),
          withTiming(0.25, { duration: 800, easing: Easing.inOut(Easing.quad) }),
        ),
        -1,
        false,
      )
      // Let a burst read clearly, then settle gently if no newer roll arrives.
      settleTimer = setTimeout(() => {
        cancelAnimation(pulse)
        pulse.value = withTiming(0.25, {
          duration: 1_200,
          easing: Easing.out(Easing.quad),
        })
      }, 7_000)
    } else {
      pulse.value = withTiming(0.25, {
        duration: 600,
        easing: Easing.out(Easing.quad),
      })
    }

    return () => {
      if (settleTimer) clearTimeout(settleTimer)
      cancelAnimation(pulse)
    }
  }, [activityAt, pulse, reducedMotion])

  const boltStyle = useAnimatedStyle(() => ({
    opacity: 0.4 + pulse.value * 0.6,
    transform: [{ scale: 0.94 + pulse.value * 0.14 }],
  }))

  const activityAge = formatLiveAge(activityAt, now)
  const viewedAge = formatLiveAge(viewingAt ?? activityAt, now)
  const accessibilityLabel = mode === 'live'
    ? `Dice luck live, updated ${activityAge}`
    : mode === 'history'
      ? `Viewing a past dice roll from ${viewedAge}`
      : `Dice luck syncing, last activity ${activityAge}`

  return (
    <View
      className="flex-row items-center gap-1 pl-2 shrink-0"
      accessibilityLabel={accessibilityLabel}
    >
      <Animated.View style={boltStyle}>
        <MaterialCommunityIcons
          name="lightning-bolt"
          size={15}
          color={theme === 'dark' ? '#f4efd5' : '#18170f'}
        />
      </Animated.View>
      {mode === 'live' ? (
        <Text className="font-stencil text-[9px] uppercase tracking-wider text-blood">Live</Text>
      ) : mode === 'history' ? (
        <>
          <Text numberOfLines={1} className="font-stencil text-[9px] uppercase tracking-wider text-ink">Viewing past</Text>
          <TouchableOpacity
            onPress={onGoLive}
            accessibilityRole="button"
            accessibilityLabel="Go live with dice luck"
            activeOpacity={0.7}
            className="rounded-full border border-blood bg-blood px-2 py-0.5"
          >
            <Text className="font-stencil text-[9px] uppercase tracking-wider text-white">Go live</Text>
          </TouchableOpacity>
        </>
      ) : (
        <Text className="font-stencil text-[9px] uppercase tracking-wider text-ink-soft">Syncing…</Text>
      )}
    </View>
  )
}

function sampleIndices(length: number, maxPoints = 120): number[] {
  if (length <= maxPoints) return Array.from({ length }, (_, index) => index)
  return Array.from({ length: maxPoints }, (_, index) =>
    Math.round(index / (maxPoints - 1) * (length - 1)))
}

const CHART_PLOT_LEFT = 13
const CHART_PLOT_RIGHT = 21

export function sliderIndexForOffset(offset: number, width: number, pointCount: number): number {
  if (pointCount <= 1 || width <= 0) return 0
  const ratio = Math.max(0, Math.min(1, offset / width))
  return Math.round(ratio * (pointCount - 1))
}

/** X offset for a real roll, matching the chart's synthetic zero point and insets. */
export function chartPointOffset(index: number, width: number, pointCount: number): number {
  if (width <= 0 || pointCount <= 0) return 0
  if (pointCount === 1) return width - CHART_PLOT_RIGHT
  const plotWidth = width - CHART_PLOT_LEFT - CHART_PLOT_RIGHT
  return CHART_PLOT_LEFT + (index + 1) / pointCount * plotWidth
}

/** Map a tap inside the inset SVG plot to the exact unsampled series index. */
export function chartIndexForOffset(
  offset: number,
  width: number,
  pointCount: number,
  plotLeft = CHART_PLOT_LEFT,
  plotRight = CHART_PLOT_RIGHT,
): number {
  if (pointCount <= 1 || width <= plotLeft + plotRight) return 0
  const plotWidth = width - plotLeft - plotRight
  // The luck line includes a synthetic zero at x=plotLeft, so the first real
  // roll begins one interval into a pointCount-interval plot.
  const firstPointX = plotLeft + plotWidth / pointCount
  const lastPointX = width - plotRight
  return sliderIndexForOffset(offset - firstPointX, lastPointX - firstPointX, pointCount)
}

function ChartScrubber({
  pointCount,
  selectedIndex,
  chartWidth,
  chartLeft,
  onChange,
}: {
  pointCount: number
  selectedIndex: number
  chartWidth: number
  chartLeft: number
  onChange: (index: number) => void
}) {
  const [trackLayout, setTrackLayout] = React.useState({ width: 0, left: 0 })
  const trackWidth = trackLayout.width
  const coordinateWidth = chartWidth > 0 ? chartWidth : trackWidth
  const selectFromGesture = (event: GestureResponderEvent) => {
    const chartOffset = chartWidth > 0 && trackWidth > 0
      ? (event.nativeEvent.locationX + trackLayout.left - chartLeft) / trackWidth * chartWidth
      : event.nativeEvent.locationX
    onChange(chartIndexForOffset(chartOffset, coordinateWidth, pointCount))
  }
  const handleLayout = (event: LayoutChangeEvent) => setTrackLayout({
    width: event.nativeEvent.layout.width,
    left: event.nativeEvent.layout.x ?? 0,
  })

  return (
    <View
      testID="dice-luck-chart-scrubber"
      accessible
      accessibilityRole="adjustable"
      accessibilityLabel="Choose a dice luck point"
      accessibilityValue={{ min: 1, max: pointCount, now: selectedIndex + 1 }}
      accessibilityActions={[{ name: 'increment' }, { name: 'decrement' }]}
      onAccessibilityAction={(event) => {
        const delta = event.nativeEvent.actionName === 'increment' ? 1 : -1
        onChange(Math.max(0, Math.min(pointCount - 1, selectedIndex + delta)))
      }}
      onLayout={handleLayout}
      onStartShouldSetResponder={() => true}
      onMoveShouldSetResponder={() => true}
      onResponderGrant={selectFromGesture}
      onResponderMove={selectFromGesture}
      hitSlop={{ top: 8, bottom: 8 }}
      className="h-5 justify-center"
      style={chartWidth > 0
        ? { width: chartWidth, marginLeft: chartLeft, alignSelf: 'flex-start' }
        : undefined}
    >
      <View className="h-1 rounded-full bg-olive/40" pointerEvents="none" />
    </View>
  )
}

function LuckLineChart({
  series,
  compact,
  selectedIndex,
  onSelectIndex,
  onViewSelected,
}: {
  series: number[]
  compact: boolean
  selectedIndex: number
  onSelectIndex: (index: number) => void
  onViewSelected?: () => void
}) {
  const { theme } = useTheme()
  const [chartLayout, setChartLayout] = React.useState({ width: 0, left: 0 })
  const chartWidth = chartLayout.width
  // Match the SVG coordinate system to its rendered width. A fixed 300-wide
  // viewBox is letterboxed by SVG on desktop, leaving the series compressed in
  // the middle of an otherwise full-width chart.
  const width = chartWidth > 0 ? chartWidth : 300
  // Whole physical pixels (see snapToPixel): 84pt is 220.5px at Android's
  // 2.625 density, and Yoga rounds the half pixel either way between passes.
  const height = snapToPixel(compact ? 70 : 84)
  const plotHeight = compact ? 47 : 61
  const gutterTop = compact ? 49 : 63
  const plotLeft = CHART_PLOT_LEFT
  // Keep enough room for a signed luck label at the final point. Without a
  // right plot inset the SVG has to clamp the label inward, separating it
  // from the cursor it describes.
  const plotRight = CHART_PLOT_RIGHT
  const sampleAt = sampleIndices(series.length)
  // Downsample only the rendered paths. Domains must include every value so an
  // unrendered outlier cannot push the selected cursor outside the SVG.
  const luckMin = Math.min(0, ...series)
  const luckMax = Math.max(0, ...series)
  const luckSpan = Math.max(luckMax - luckMin, 1)
  const luckY = (value: number) => 6 + (luckMax - value) / luckSpan * (plotHeight - 12)
  const x = (index: number, count: number) => count === 1
    ? plotLeft
    : plotLeft + index / (count - 1) * (width - plotLeft - plotRight)
  const luckPoints = [
    `${x(0, series.length + 1)},${luckY(0)}`,
    ...sampleAt.map((index) => `${x(index + 1, series.length + 1)},${luckY(series[index]!)}`),
  ].join(' ')
  const luckLine = theme === 'dark' ? '#e6e1c4' : '#18170f'
  const baseline = theme === 'dark' ? '#8a8c6e' : '#aaa181'
  const cursorGuide = theme === 'dark' ? '#c8c4a8' : '#aaa181'
  const baselineLabelY = Math.max(8, Math.min(plotHeight - 2, luckY(0) + 3))
  const selectedX = x(selectedIndex + 1, series.length + 1)
  const selectedLuck = series[selectedIndex] ?? series.at(-1) ?? 0
  const luckLabel = formatDiceLuckSwing(selectedLuck)
  const luckLabelWidth = luckLabel.length * 5.5
  const labelGroupLeft = Math.max(3, Math.min(
    width - luckLabelWidth - 3,
    selectedX - luckLabelWidth / 2,
  ))
  const selectFromChart = (event: GestureResponderEvent) => {
    onSelectIndex(chartIndexForOffset(
      event.nativeEvent.locationX,
      chartWidth,
      series.length,
      plotLeft,
      plotRight,
    ))
  }

  return (
    <View className="gap-0 relative">
      <View
        accessibilityLabel={`Cumulative dice swing chart ending at ${formatDiceLuckSwing(series.at(-1) ?? 0)} armies. Tap or drag to inspect a roll.`}
        onLayout={(event) => setChartLayout({
          width: event.nativeEvent.layout.width,
          left: event.nativeEvent.layout.x ?? 0,
        })}
        onStartShouldSetResponder={() => true}
        onMoveShouldSetResponder={() => true}
        onResponderGrant={selectFromChart}
        onResponderMove={selectFromChart}
        className="self-stretch rounded-lg border border-olive bg-paper-up overflow-hidden"
        style={{ height }}
      >
        <Svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
        >
          <Line
            x1={plotLeft}
            x2={width}
            y1={luckY(0)}
            y2={luckY(0)}
            stroke={baseline}
            strokeWidth={2}
            strokeDasharray="1 5"
            strokeLinecap="round"
          />
          <SvgText
            x={plotLeft - 3}
            y={baselineLabelY}
            fill={baseline}
            fontSize={8}
            fontWeight="700"
            textAnchor="end"
          >
            0
          </SvgText>
          <Polyline points={luckPoints} fill="none" stroke={luckLine} strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" />
          <Line
            x1={selectedX}
            x2={selectedX}
            y1={3}
            y2={gutterTop - 3}
            stroke={cursorGuide}
            strokeWidth={2}
            strokeDasharray="1 5"
            strokeLinecap="round"
          />
          <Circle cx={selectedX} cy={luckY(selectedLuck)} r={4} fill={luckLine} />
          <SvgText
            x={labelGroupLeft + luckLabelWidth / 2}
            y={gutterTop + 13.5}
            fill={luckLine}
            fontSize={9.5}
            fontWeight="700"
            textAnchor="middle"
          >
            {luckLabel}
          </SvgText>
        </Svg>
      </View>
      <ChartScrubber
        pointCount={series.length}
        selectedIndex={selectedIndex}
        chartWidth={chartWidth}
        chartLeft={chartLayout.left}
        onChange={onSelectIndex}
      />
      <View
        testID="dice-luck-chart-scrubber-thumb"
        pointerEvents="none"
        className="absolute w-4 h-4 rounded-full bg-blood border-2 border-paper"
        style={{
          left: `${selectedX / width * 100}%`,
          top: height + 2,
          marginLeft: -8,
        }}
      />
      <View className="-mt-0.5 flex-row items-center justify-between gap-3">
        <Text className="font-typed text-[10px] text-ink-soft">
          Roll {selectedIndex + 1}/{series.length}
        </Text>
        {onViewSelected ? (
          <TouchableOpacity
            onPress={onViewSelected}
            accessibilityRole="link"
            accessibilityLabel="View selected move on map"
            activeOpacity={0.7}
          >
            <Text className="font-stencil text-[9px] uppercase tracking-wider text-blood">
              View move →
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  )
}

function DiceLuckBreakdown({
  rows, names, live, active, error, onBack,
}: {
  rows: readonly DiceLuckPlayerSummary[]
  names: ReadonlyMap<string, string>
  live: boolean
  active: boolean
  error: boolean
  onBack: () => void
}) {
  return (
    <ScrollView testID="dice-luck-breakdown" contentContainerStyle={{ padding: 16, gap: 12 }}>
      <TouchableOpacity onPress={onBack} accessibilityRole="button" accessibilityLabel="Back to dice luck help">
        <Text className="font-stencil text-xs text-blood">← Back</Text>
      </TouchableOpacity>
      <Text className="font-stencil text-xl text-ink">Live player calculations</Text>
      {error || (active && !live) ? (
        <Text className="font-typed text-xs text-ink-soft">
          {error ? 'Refresh failed — showing last complete totals.' : 'Reconnecting / syncing · last complete totals'}
        </Text>
      ) : null}
      {rows.map((row, index) => (
        <View key={row.playerId} testID={`dice-luck-breakdown-${row.playerId}`}
          className="rounded-xl border border-olive bg-paper-up p-3 gap-2">
          <Text className="font-stencil text-sm text-ink">
            #{index + 1} {names.get(row.playerId) ?? `Player ${row.playerId}`}
          </Text>
          <Text className="font-typed text-xs text-ink-soft">
            {row.rollCount} involved rolls · {row.attackRollCount} attacking · {row.defenseRollCount} defending
          </Text>
          <Text className="font-typed text-sm text-ink">
            Army swing: {formatDiceLuckSwing(row.attackSwing)} attacking + ({formatDiceLuckSwing(row.defenseSwing)}) defending = {formatDiceLuckSwing(row.netSwing)} armies
          </Text>
          <Text className="font-typed text-sm text-ink">
            Total variance: {row.variance.toFixed(2)} armies²{'\n'}
            Standard deviation: √{row.variance.toFixed(2)} = {Math.sqrt(row.variance).toFixed(2)} armies
          </Text>
          <Text className="font-typed text-sm text-ink">
            {row.zScore === null ? 'Standardized luck unavailable: no variable dice outcomes yet.'
              : `z = ${row.netSwing.toFixed(2)} ÷ ${Math.sqrt(row.variance).toFixed(2)} = ${row.zScore.toFixed(3)}`}
          </Text>
          <Text testID={`dice-luck-breakdown-score-${row.playerId}`} className="font-stencil text-base text-ink">
            {row.zScore === null ? 'Luck Score unavailable'
              : `100 + 20 × (${row.zScore.toFixed(3)}) = ${formatDiceLuckScore(row.zScore, row.rollCount)} Luck Score`}
          </Text>
          <Text className={`font-stencil text-xs ${diceLuckVerdictToneClass(row.verdict)}`}>
            {formatDiceLuckVerdict(row.verdict)}
          </Text>
          {row.rollCount > 0 && row.rollCount < DICE_LUCK_PERCENTILE_MIN_ROLLS ? (
            <Text className="font-typed text-[10px] text-ink-soft">Early results</Text>
          ) : null}
        </View>
      ))}
    </ScrollView>
  )
}

export function DiceLuckModal({
  visible,
  onClose,
  players,
  viewerPlayerId,
  initialPlayerId,
  result,
  snapshot,
  canGoLive,
  isLive,
  lastUpdatedAt,
  loading,
  error,
  unavailable,
  onGoLive,
  onViewEvent,
}: Props) {
  const insets = useSafeAreaInsets()
  const { width, height } = useWindowDimensions()
  const protectedTop = React.useContext(GameOverlayTopContext)
  const panelBounds = floatingGamePanelBounds(height, insets.top, insets.bottom, protectedTop)
  const useWideColumns = width >= 700
  const rows = React.useMemo(
    () => (result?.players ?? snapshot?.players ?? []).map(withCurrentDiceLuckQualification),
    [result?.players, snapshot?.players],
  )
  const hasAnyRolls = rows.some((row) => row.rollCount > 0)
  const detailsByPlayer = React.useMemo(
    () => new Map(result?.players.map((row) => [row.playerId, row]) ?? []),
    [result?.players],
  )
  const nameByPlayer = React.useMemo(
    () => new Map(players.map((player) => [String(player.slot), player.displayName])),
    [players],
  )
  const rankedRows = React.useMemo(() => rankDiceLuckPlayers(rows), [rows])
  const luckRankByPlayer = React.useMemo(
    () => new Map(rankedRows.map((row, index) => [row.playerId, index + 1])),
    [rankedRows],
  )
  const [selectedPlayerId, setSelectedPlayerId] = React.useState<string | null>(null)
  const [showBreakdown, setShowBreakdown] = React.useState(false)
  const [showHelp, setShowHelp] = React.useState(false)
  const [chartCursor, setChartCursor] = React.useState<{
    playerId: string | null
    index: number
    followLatest: boolean
  }>({ playerId: null, index: 0, followLatest: true })

  React.useEffect(() => {
    if (!visible) {
      setSelectedPlayerId(null)
      setShowBreakdown(false)
      setShowHelp(false)
      setChartCursor({ playerId: null, index: 0, followLatest: true })
      return
    }
    const preferredId = initialPlayerId && rankedRows.some((row) => row.playerId === initialPlayerId)
      ? initialPlayerId
      : rankedRows.some((row) => row.playerId === viewerPlayerId)
        ? viewerPlayerId
        : rankedRows[0]?.playerId ?? null
    setSelectedPlayerId((current) =>
      current && rankedRows.some((row) => row.playerId === current)
        ? current
        : preferredId,
    )
  }, [initialPlayerId, rankedRows, viewerPlayerId, visible])

  const selectedRow = rankedRows.find((row) => row.playerId === selectedPlayerId) ?? rankedRows[0] ?? null
  const selectedDetails = selectedRow ? detailsByPlayer.get(selectedRow.playerId) : undefined
  const selectedName = selectedRow
    ? nameByPlayer.get(selectedRow.playerId) ?? `Player ${selectedRow.playerId}`
    : ''
  const chartPointCount = selectedDetails?.series.length ?? 0
  React.useEffect(() => {
    const playerId = selectedRow?.playerId ?? null
    const latestIndex = Math.max(0, chartPointCount - 1)
    setChartCursor((current) => {
      if (current.playerId !== playerId) {
        return { playerId, index: latestIndex, followLatest: true }
      }
      if (current.followLatest) {
        return { ...current, index: latestIndex }
      }
      return { ...current, index: Math.min(current.index, latestIndex) }
    })
  }, [chartPointCount, selectedRow?.playerId])
  const chartPointIndex = Math.min(
    chartCursor.playerId === selectedRow?.playerId ? chartCursor.index : Math.max(0, chartPointCount - 1),
    Math.max(0, chartPointCount - 1),
  )
  const chartIsFollowingLive = chartCursor.playerId !== selectedRow?.playerId || chartCursor.followLatest
  const selectedSeriesEvent = selectedDetails?.seriesEvents?.[chartPointIndex]
  const selectedPlayerLastRollAt = selectedDetails?.seriesEvents?.at(-1)?.ts ?? lastUpdatedAt
  const selectedTimelineRollCount = selectedDetails ? chartPointIndex + 1 : selectedRow?.rollCount ?? 0
  const selectedTimelineSwing = selectedDetails?.series[chartPointIndex] ?? selectedRow?.netSwing ?? 0
  const selectedTimelineAttackSwing = selectedDetails?.attackSeries[chartPointIndex] ?? selectedRow?.attackSwing ?? 0
  const selectedTimelineDefenseSwing = selectedDetails?.defenseSeries[chartPointIndex] ?? selectedRow?.defenseSwing ?? 0
  const selectedTimelineVariance = selectedDetails?.varianceSeries[chartPointIndex] ?? selectedRow?.variance ?? 0
  const selectedTimelineZScore = selectedTimelineVariance > 0
    ? selectedTimelineSwing / Math.sqrt(selectedTimelineVariance)
    : null
  const selectedTimelinePercentile = selectedDetails
    ? selectedTimelineZScore === null
      ? null
      : diceLuckPercentile(selectedTimelineZScore, selectedTimelineRollCount)
    : selectedRow?.percentile ?? null
  const selectedTimelineVerdict = selectedDetails
    ? selectedTimelineZScore === null
      ? null
      : diceLuckVerdict(selectedTimelineZScore, selectedTimelineRollCount)
    : selectedRow?.verdict ?? null
  const goLive = () => {
    if (!selectedRow || chartPointCount === 0) return
    setChartCursor({
      playerId: selectedRow.playerId,
      index: chartPointCount - 1,
      followLatest: true,
    })
    onGoLive()
  }
  const dismissAndThen = useWebModalHistory({ visible, canDismiss: true, onDismiss: onClose })
  React.useEffect(() => {
    if (Platform.OS !== 'android' || !visible) return
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showBreakdown) {
        setShowBreakdown(false)
        return true
      }
      if (showHelp) {
        setShowHelp(false)
        return true
      }
      onClose()
      return true
    })
    return () => subscription.remove()
  }, [onClose, showHelp, showBreakdown, visible])

  return (
    <AppModal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <View
        className="flex-1 bg-black/60 items-center justify-start"
        style={{ paddingTop: panelBounds.paddingTop, paddingBottom: panelBounds.paddingBottom, paddingHorizontal: 16 }}
      >
        <Pressable
          accessible={false}
          onPress={onClose}
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
        />
        <View
          style={{
            width: '100%',
            maxWidth: useWideColumns ? 920 : 620,
            maxHeight: panelBounds.maxHeight,
            overflow: 'visible',
          }}
        >
          <ModalTabSurface
            accessible={false}
            accessibilityLabel="Dice luck"
            style={{ maxHeight: panelBounds.maxHeight }}
          >
          {showBreakdown ? (
            <DiceLuckBreakdown rows={rankedRows} names={nameByPlayer} live={isLive} active={canGoLive} error={error} onBack={() => setShowBreakdown(false)} />
          ) : showHelp ? (
            <DiceLuckHelp onBack={() => setShowHelp(false)} onShowCalculations={() => setShowBreakdown(true)} />
          ) : loading && !snapshot && !result ? (
            <View className="items-center justify-center gap-3 px-6 py-14">
              <ActivityIndicator size="large" color="#8a8c6e" />
              <Text className="font-typed text-sm text-ink-soft text-center">Reading this game&apos;s dice rolls…</Text>
            </View>
          ) : error && !snapshot && !result ? (
            <View className="items-center justify-center gap-2 px-6 py-14">
              <Text className="font-stencil text-base text-ink text-center">Couldn&apos;t load dice luck</Text>
              <Text className="font-typed text-sm text-ink-soft text-center">Close this window and try again.</Text>
            </View>
          ) : unavailable ? (
            <View className="items-center justify-center gap-2 px-6 py-14">
              <Text className="font-stencil text-base text-ink text-center">Luck wasn&apos;t recorded</Text>
              <Text className="font-typed text-sm text-ink-soft text-center">
                This game&apos;s detailed rolls expired before durable luck stats were added.
              </Text>
            </View>
          ) : (
            <ScrollView contentContainerStyle={{
              padding: useWideColumns ? 20 : 12,
              gap: useWideColumns ? 16 : 10,
            }}>
              {error && (result || snapshot) && (
                <Text className="font-typed text-xs text-ink-soft text-center">
                  Couldn&apos;t refresh just now. Showing the last complete result.
                </Text>
              )}
              {!hasAnyRolls ? (
                <View className="items-center justify-center gap-2 px-6 py-10">
                  <Text className="font-stencil text-base text-ink text-center">No attack rolls yet</Text>
                  <Text className="font-typed text-sm text-ink-soft text-center">
                    Dice luck will appear after the first battle roll.
                  </Text>
                </View>
              ) : selectedRow && (
                <View
                  testID="dice-luck-summary-card"
                  className={`rounded-xl border-2 border-olive bg-paper-up ${
                    useWideColumns ? 'px-4 py-4 gap-3' : 'px-3 py-3 gap-2'
                  }`}
                >
                  <View className="gap-1">
                    <View className="flex-row items-center justify-between gap-2">
                      <Text numberOfLines={1} className="font-stencil text-[10px] uppercase tracking-widest text-ink-soft shrink">
                        {selectedRow.playerId === viewerPlayerId ? 'Your dice luck' : `${selectedName}'s dice luck`}
                      </Text>
                      {canGoLive && selectedPlayerLastRollAt !== null ? (
                        <DiceActivityStatus
                          activityAt={selectedPlayerLastRollAt}
                          viewingAt={selectedSeriesEvent?.ts ?? null}
                          mode={!chartIsFollowingLive ? 'history' : isLive ? 'live' : 'syncing'}
                          onGoLive={goLive}
                        />
                      ) : null}
                    </View>
                    <View className="flex-row items-end justify-between gap-3">
                      <View className="flex-1 gap-0.5">
                        <Text testID="dice-luck-summary-score" accessibilityLabel={`Luck Score ${formatDiceLuckScore(selectedTimelineZScore, selectedTimelineRollCount)}`} className="font-stencil text-2xl text-ink">{formatDiceLuckScore(selectedTimelineZScore, selectedTimelineRollCount)}</Text>
                        <Text className="font-typed text-[10px] text-ink-soft">(100 = expected)</Text>
                      </View>
                      <View className="items-end">
                        <Text
                          testID="dice-luck-summary-verdict"
                          className={`font-stencil text-sm uppercase ${diceLuckVerdictToneClass(selectedTimelineVerdict)}`}
                        >
                          {formatDiceLuckVerdict(selectedTimelineVerdict)}
                        </Text>
                        <Text className="font-typed text-xs text-ink-soft">{selectedTimelineRollCount} involved rolls</Text>
                      </View>
                    </View>
                  </View>
                  <View className="gap-1">
                    {selectedDetails?.series.length ? (
                      <LuckLineChart
                        series={selectedDetails.series}
                        compact={!useWideColumns}
                        selectedIndex={chartPointIndex}
                        onSelectIndex={(index) => {
                          setChartCursor({
                            playerId: selectedRow.playerId,
                            index,
                            followLatest: false,
                          })
                        }}
                        onViewSelected={selectedSeriesEvent
                          ? () => dismissAndThen(() => onViewEvent(
                              selectedSeriesEvent.eventIndex,
                              selectedSeriesEvent.ts,
                            ))
                          : undefined}
                      />
                    ) : (
                      <Text className="font-typed text-xs text-ink-soft italic">
                        The cumulative chart is unavailable after the detailed log expires.
                      </Text>
                    )}
                    <View testID="dice-luck-split-stats" className="flex-row gap-2">
                      <View
                        testID="dice-luck-attacking-stat"
                        className="flex-1 items-center justify-center rounded-lg border border-olive bg-paper-up px-3 py-2"
                      >
                        <Text className="font-stencil text-[10px] uppercase tracking-wider text-center text-ink-soft">
                          Attacking
                        </Text>
                        <Text className="font-stencil text-xl leading-6 text-center text-ink">
                          {formatDiceLuckSwing(selectedTimelineAttackSwing)}
                        </Text>
                      </View>
                      <View
                        testID="dice-luck-defending-stat"
                        className="flex-1 items-center justify-center rounded-lg border border-olive bg-paper-up px-3 py-2"
                      >
                        <Text className="font-stencil text-[10px] uppercase tracking-wider text-center text-ink-soft">
                          Defending
                        </Text>
                        <Text className="font-stencil text-xl leading-6 text-center text-ink">
                          {formatDiceLuckSwing(selectedTimelineDefenseSwing)}
                        </Text>
                      </View>
                      <View
                        testID="dice-luck-total-stat"
                        className="flex-1 items-center justify-center rounded-lg border border-olive bg-paper-up px-2 py-2"
                      >
                        <Text numberOfLines={1} className="font-stencil text-[9px] uppercase text-center text-ink-soft">Total armies</Text>
                        <Text testID="dice-luck-summary-swing" className="font-stencil text-xl leading-6 text-center text-ink">{formatDiceLuckSwing(selectedTimelineSwing)}</Text>
                      </View>
                    </View>
                  </View>
                  <View className="mt-1 flex-row items-center gap-3">
                    {selectedTimelinePercentile !== null ? (
                      <Text className="flex-1 font-typed text-xs text-ink">
                        Estimated: luckier than {Math.round(selectedTimelinePercentile)}% of comparable rolls.
                      </Text>
                    ) : (
                      <Text className="flex-1 font-typed text-xs text-ink-soft">
                        Early results — the score and label may shift quickly with more rolls. Estimated percentiles appear at {DICE_LUCK_PERCENTILE_MIN_ROLLS} rolls.
                      </Text>
                    )}
                    <HelpButton
                      testID="dice-luck-help-button"
                      onPress={() => setShowHelp(true)}
                      accessibilityLabel="How dice luck works"
                      size={useWideColumns ? 38 : 34}
                    />
                  </View>
                </View>
              )}

              {hasAnyRolls && selectedRow && (
                <View
                  testID="dice-luck-lower-panel"
                  className="gap-2"
                >
                    <View testID="dice-luck-player-list" className="gap-2">
                      <View className="flex-row items-center justify-between gap-3">
                        <Text className="font-stencil text-xs uppercase tracking-widest text-ink-soft">Players</Text>
                      </View>
                      {Array.from(
                        { length: rankedRows.length >= 4 ? Math.ceil(rankedRows.length / 2) : rankedRows.length },
                        (_, rowIndex) => {
                          if (rankedRows.length < 4) return rankedRows.slice(rowIndex, rowIndex + 1)
                          return rankedRows.slice(rowIndex * 2, rowIndex * 2 + 2)
                        },
                      ).map((playerRow, playerRowIndex) => (
                        <View
                          key={playerRow.map((row) => row.playerId).join('-')}
                          testID={`dice-luck-player-row-${playerRowIndex}`}
                          className="flex-row gap-2"
                        >
                          {playerRow.map((row) => {
                            const index = rankedRows.indexOf(row)
                            const name = nameByPlayer.get(row.playerId) ?? `Player ${row.playerId}`
                            const selected = row.playerId === selectedRow.playerId
                            return (
                              <View
                                key={row.playerId}
                                testID={`dice-luck-player-column-${row.playerId}`}
                                className="flex-1 min-w-0"
                                style={{ flex: 1, width: 0 }}
                              >
                                <View
                                  testID={`dice-luck-player-card-${row.playerId}`}
                                  className={`rounded-xl border-2 px-3 py-2 gap-1 ${
                                    selected ? 'border-blood bg-paper' : 'border-olive bg-paper-up'
                                  }`}
                                  style={{ minHeight: 94 }}
                                >
                                  <TouchableOpacity
                                    onPress={() => setSelectedPlayerId(row.playerId)}
                                    accessibilityRole="button"
                                    accessibilityLabel={`View ${name} dice luck, rank ${index + 1}`}
                                    accessibilityState={{ selected }}
                                    activeOpacity={0.75}
                                    className="gap-0.5"
                                  >
                                    <View className="flex-row gap-2 items-end">
                                      <View className="flex-1 min-w-0">
                                        <Text
                                          testID={`dice-luck-rank-${row.playerId}`}
                                          numberOfLines={1}
                                          className={`font-stencil text-xs uppercase tracking-wider ${
                                          selected ? 'text-blood' : 'text-ink-soft'
                                          }`}
                                        >
                                          #{luckRankByPlayer.get(row.playerId) ?? index + 1}
                                        </Text>
                                        <Text className="text-ink font-typed text-sm" numberOfLines={1}>
                                          {name}{row.playerId === viewerPlayerId ? ' (you)' : ''}
                                        </Text>
                                      </View>
                                      <Text testID={`dice-luck-score-${row.playerId}`} accessibilityLabel={`Luck Score ${formatDiceLuckScore(row.zScore, row.rollCount)}`} className="font-stencil text-base text-ink">{formatDiceLuckScore(row.zScore, row.rollCount)}</Text>
                                    </View>
                                    <Text testID={`dice-luck-swing-${row.playerId}`} className="font-typed text-[11px] text-ink-soft" numberOfLines={1}>
                                      {formatDiceLuckSwing(row.netSwing)} armies · {row.rollCount} rolls
                                    </Text>
                                    <View className="flex-row items-center justify-between gap-1">
                                      <Text
                                        testID={`dice-luck-verdict-${row.playerId}`}
                                        numberOfLines={1}
                                        className={`font-stencil text-xs uppercase flex-shrink ${diceLuckVerdictToneClass(row.verdict)}`}
                                      >
                                        {displayVerdict(row)}
                                      </Text>
                                      {row.rollCount > 0 && row.rollCount < DICE_LUCK_PERCENTILE_MIN_ROLLS ? (
                                        <Text numberOfLines={1} className="font-typed text-[9px] text-ink-soft">Early results</Text>
                                      ) : null}
                                    </View>
                                  </TouchableOpacity>
                                </View>
                              </View>
                            )
                          })}
                          {rankedRows.length >= 4 && playerRow.length === 1 ? (
                            <View
                              testID={`dice-luck-player-spacer-${playerRowIndex}`}
                              style={{ flex: 1, width: 0 }}
                            />
                          ) : null}
                        </View>
                      ))}
                    </View>
                </View>
              )}
            </ScrollView>
          )}
          </ModalTabSurface>
          <ModalCloseTab
            onPress={onClose}
            accessibilityLabel="Close dice luck"
            testID="dice-luck-close"
          />
        </View>
      </View>
    </AppModal>
  )
}
